import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Sparkles } from 'lucide-react';
import type { ReactNode } from 'react';
import { useDecisionActionsDisabled } from '../composer-gate-context';

export interface DecisionFooterProps {
  content?: ReactNode;
  /** Keep chat available while the draft needs information before approval. */
  allowMessage?: boolean;
  /** The card's own confirmation sentence, e.g. "I'll launch this ad now." */
  sentence: ReactNode;
  onAccept: () => void;
  onReject: () => void;
  acceptLabel?: string;
  rejectLabel?: string;
  /** Present only when the card offers a way to change the proposal first. */
  onEdit?: () => void;
  editLabel?: string;
  /** Disables every button while a mutation triggered by this footer is in flight. */
  busy?: boolean;
  /** Extra Accept-specific disable, e.g. a validation rule (Reject/Edit stay clickable). */
  acceptDisabled?: boolean;
  /** Actionable explanation shown even though the button is disabled. */
  acceptDisabledReason?: string;
  /**
   * Renders only the button row, no sentence paragraph — for a card that
   * already shows its own sentence at the top and only wants the shared
   * footer for its buttons. The composer slot always renders the full block
   * (sentence + buttons together), since it has no card header of its own.
   */
  hideSentence?: boolean;
  /** Additional editing actions for cards with more than one editor. */
  additionalActions?: Array<{
    label: string;
    onClick: () => void;
    disabled?: boolean;
  }>;
}

/**
 * The one approval-footer shape used by every decision card in the assistant
 * chat: a Sparkles sentence plus Edit (optional, left) / Reject / Accept
 * (right). Rendered both inline in the card AND inside the composer slot
 * (`useComposerGate`) while the card is unresolved — one implementation, so
 * the two can't drift apart.
 */
export function DecisionFooter({
  sentence,
  onAccept,
  onReject,
  acceptLabel = 'Accept',
  rejectLabel = 'Reject',
  onEdit,
  editLabel = 'Edit',
  busy: actionPending = false,
  acceptDisabled = false,
  acceptDisabledReason,
  hideSentence = false,
  additionalActions,
  content,
}: DecisionFooterProps) {
  const actionsDisabled = useDecisionActionsDisabled();
  const busy = actionPending || actionsDisabled;
  if (content)
    return (
      <div
        data-testid="decision-footer"
        className="max-h-[60dvh] overflow-y-auto [&>div]:max-w-none [&>div]:border-0 [&>div]:p-0"
      >
        {content}
      </div>
    );
  const acceptButton = (
    <Button size="sm" disabled={busy || acceptDisabled} onClick={onAccept}>
      {acceptLabel}
    </Button>
  );
  const disabledReason = busy
    ? 'Wait for the current action to finish.'
    : acceptDisabledReason;
  return (
    <div data-testid="decision-footer" className="w-full">
      {!hideSentence && (
        <div className="flex items-start gap-2 text-xs">
          <Sparkles className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
          <p>{sentence}</p>
        </div>
      )}
      <div
        className={
          hideSentence
            ? 'flex flex-wrap items-center justify-between gap-2'
            : 'mt-3 flex flex-wrap items-center justify-between gap-2'
        }
      >
        <div className="flex flex-wrap gap-1">
          {onEdit ? (
            <Button variant="ghost" size="sm" disabled={busy} onClick={onEdit}>
              {editLabel}
            </Button>
          ) : (
            <div />
          )}
          {additionalActions?.map((action) => (
            <Button
              key={action.label}
              variant="ghost"
              size="sm"
              disabled={busy || action.disabled}
              onClick={action.onClick}
            >
              {action.label}
            </Button>
          ))}
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" disabled={busy} onClick={onReject}>
            {rejectLabel}
          </Button>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                tabIndex={
                  (busy || acceptDisabled) && disabledReason ? 0 : undefined
                }
                aria-label={
                  (busy || acceptDisabled) && disabledReason
                    ? `Why ${acceptLabel} is unavailable`
                    : undefined
                }
                className={
                  busy || acceptDisabled
                    ? 'inline-flex cursor-not-allowed'
                    : 'inline-flex'
                }
              >
                {acceptButton}
              </span>
            </TooltipTrigger>
            {(busy || acceptDisabled) && disabledReason && (
              <TooltipContent className="max-w-xs">
                {disabledReason}
              </TooltipContent>
            )}
          </Tooltip>
        </div>
      </div>
    </div>
  );
}
